import {
    type PairFieldColumn,
    PairFieldRows,
    type PairFieldValue
} from "@solomon-apps/shared/src/components/ui/pair-field-rows";
import React from "react";

export interface Tranche {
    /** Tier threshold — the amount/duration at which the boost applies. */
    threshold: string;
    /** Reward boost multiplier at this tier. */
    boost: string;
}

/** A boost is out of range when it's set and falls outside [min, max]. */
function boostOutOfRange(boost: string, min: number, max: number): boolean {
    if (boost.trim() === "") return false;
    const n = Number(boost);
    return Number.isNaN(n) || n < min || n > max;
}

/**
 * True when the schedule can't be saved — any row is incomplete (missing
 * threshold or boost) or its boost violates the issuer's [min, max] range.
 */
export function tranchesHaveError(
    tranches: Tranche[],
    boostMin: number,
    boostMax: number
): boolean {
    return tranches.some(
        (t) =>
            t.threshold.trim() === "" ||
            t.boost.trim() === "" ||
            boostOutOfRange(t.boost, boostMin, boostMax)
    );
}

interface TrancheEditorProps {
    tranches: Tranche[];
    onChange: (next: Tranche[]) => void;
    /** Unit for the threshold field (e.g. "value", "days"). */
    thresholdUnit: string;
    /** Optional icon shown before the threshold unit. */
    thresholdIcon?: string;
    /** Issuer's allowed boost range — boosts outside it error. */
    boostMin: number;
    boostMax: number;
    /** Unit for the boost field. Defaults to "x". */
    boostUnit?: string;
    disabled?: boolean;
}

/**
 * TrancheEditor — a vertically-stacked, table-like editor for a reward
 * schedule: each row is a `{ threshold, boost }` tier. A thin policy wrapper
 * over the generic `PairFieldRows` element: it supplies the Tier/Boost
 * headers, the optional icon + unit on the threshold column, the boost
 * out-of-range validation, and the "+ Add Tranche" label. The boost
 * validates against the issuer's allowed range — out-of-range rows flag red
 * and block save.
 */
export function TrancheEditor({
    tranches,
    onChange,
    thresholdUnit,
    thresholdIcon,
    boostMin,
    boostMax,
    boostUnit = "x",
    disabled = false
}: TrancheEditorProps): React.ReactElement {
    const left: PairFieldColumn = {
        label: "Tier",
        unit: thresholdUnit,
        icon: thresholdIcon ? (
            <img src={thresholdIcon} alt="" className="size-4 rounded-full" />
        ) : undefined,
        placeholder: "0",
        inputMode: "numeric",
        sanitize: (raw) => raw.replace(/\D/g, "")
    };

    const right: PairFieldColumn = {
        label: "Boost",
        unit: boostUnit,
        placeholder: "1",
        inputMode: "decimal",
        sanitize: (raw) => raw.replace(/[^\d.]/g, ""),
        invalid: (value) => boostOutOfRange(value, boostMin, boostMax)
    };

    const rows: PairFieldValue[] = tranches.map((t) => [t.threshold, t.boost]);
    const handleChange = (next: PairFieldValue[]): void =>
        onChange(next.map(([threshold, boost]) => ({ threshold, boost })));

    return (
        <PairFieldRows
            rows={rows}
            onChange={handleChange}
            left={left}
            right={right}
            addLabel="+Add Tranche"
            removeLabel="Remove tranche"
            emptyLabel="No tranches yet — add one to start."
            disabled={disabled}
        />
    );
}
